<script setup>
/**
 * A lesson from the cards, full page (community-lesson DESIGN.md L2,
 * 2026-10-25): the /s/<short> page and the in-app open from the Community
 * (inside LessonSheet, `sheet`). Never a login wall.
 *
 * Since 2026-10-27 (lesson-ending DESIGN.md) the cream paper is the window:
 * the letter (the question leads, the lesson answers it), "✦ This spoke to
 * me" (one light act; works signed out; a second tap takes it back), then the
 * Mystic's turn and up to three more lessons on the same topic
 * (LessonEnding), all on one sheet of paper, 1000 wide at most.
 *
 * Top bar (the /s/ page only): ✦ MYSTIC AIDA (home), and for a signed-out
 * visitor a quiet "Sign in" the page doesn't need.
 *
 * Since 2026-10-28 (lesson-redo DESIGN.md rev 9) the letter is one centre
 * line, the ending is plain words with Draw a card │ 10 ✦, and More about is
 * mini cards that load in place.
 *
 * Emits: `draw` (lesson, { how: 'draw' | 'talk', href }) to open a fresh
 * session with the lesson's Mystic (E2); `own` (lesson, { words }) for the
 * sharer, back to their reading; `swap` (lesson) when a related lesson took
 * its place; `login`, `leave`.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { store } from '@/store'
import { getLessonRelated, getReadingPreview, toggleReadingSupport } from '@/api-service'
import { track } from '@/utils/analytics'
import { SPOKE_LABEL, spokeFrom } from '@/utils/lesson'
import PublicLetter from './PublicLetter.vue'
import LessonEnding from './LessonEnding.vue'
import RelatedLessons from './RelatedLessons.vue'

const props = defineProps({
  lesson: { type: Object, required: true },
  // How it was opened: 'link' (/s/) or 'community' (the feed, in-app).
  via: { type: String, default: 'link' },
  // Inside LessonSheet (the in-app open over the Community or a Mystic's
  // page): no top bar (the sheet has its ✕), no full-page height.
  sheet: { type: Boolean, default: false },
})
const emit = defineEmits(['login', 'draw', 'own', 'leave', 'swap'])

// The lesson showing: the one handed in, or a related one swapped in place.
const shown = ref(props.lesson)
watch(() => props.lesson, (l) => { shown.value = l })

const spoke = ref(false)
const spokeCount = ref(0)
watch(shown, (l) => {
  spoke.value = !!(l && (l.spoke ?? l.supported))
  spokeCount.value = Number(l && (l.spoke_count ?? l.support_count)) || 0
}, { immediate: true })
const busy = ref(false)
const signedIn = computed(() => !!store.querent_id)

async function toggleSpoke() {
  if (busy.value) return
  busy.value = true
  try {
    const r = spokeFrom(await toggleReadingSupport(shown.value.short_uuid))
    if (r) {
      spoke.value = r.spoke
      spokeCount.value = r.count
      if (r.spoke) track('lesson_resonated', { item_id: shown.value.short_uuid })
    }
  } finally { busy.value = false }
}

// E1/E2: Draw a card (or Talk it over) opens a fresh session with the Mystic.
function onDraw({ how = 'draw', href = '', mine = false } = {}) {
  // Reopening the card already drawn (``mine``) is not a draw.
  if (!mine) track('lesson_draw', { item_id: shown.value.short_uuid, how })
  emit('draw', shown.value, { how, href })
}
function onOwn({ words = '' } = {}) { emit('own', shown.value, { words }) }

// More about the same topic: fetched for the lesson showing.
const related = ref([])
let relFor = ''
async function loadRelated(short) {
  relFor = short || ''
  related.value = []
  if (!short) return
  const r = await getLessonRelated(short)
  if (relFor !== short) return
  related.value = (r && Array.isArray(r.items)) ? r.items : []
}
watch(() => shown.value && shown.value.short_uuid, (s) => { loadRelated(s) })

// A related lesson opens in place: the window goes to its top and the new
// lesson fades in. On /s/ the address follows, so a reload or a share is it.
// K1: the content fades (0.22s), the window goes to its top, the four cards
// deal again (the paper is keyed by the lesson), the address follows.
const root = ref(null)
const swapping = ref(false)
const swapped = ref(false)
const fading = ref(false)
const reducedMotion = () => typeof window !== 'undefined' && !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
const wait = (ms) => new Promise((r) => setTimeout(r, ms))
async function openRelated(item) {
  if (!item || !item.short_uuid || swapping.value) return
  swapping.value = true
  try {
    const data = await getReadingPreview(item.short_uuid, { asLesson: true })
    const next = data && data.public_version
    if (!next) return
    if (!reducedMotion() && typeof window !== 'undefined' && window.matchMedia) {
      fading.value = true
      await wait(220)
    }
    shown.value = next
    emit('swap', next)
    track('lesson_view', { item_id: next.short_uuid, via: 'related' })
    swapped.value = true
    toTop()
    if (!props.sheet) followAddress(next.short_uuid)
  } finally { swapping.value = false; fading.value = false }
}
function toTop() {
  const el = root.value
  // The sheet scrolls itself (phone) or its backdrop (desktop); the page, the window.
  let p = el && el.parentElement
  while (p) { if (p.scrollTop) p.scrollTop = 0; p = p.parentElement }
  if (!props.sheet && typeof window !== 'undefined' && window.scrollTo) { try { window.scrollTo(0, 0) } catch (e) { /* jsdom */ } }
}
function followAddress(short) {
  try {
    const u = new URL(window.location.href)
    if (u.searchParams.has('reading')) u.searchParams.set('reading', short)
    if (u.searchParams.has('rs')) u.searchParams.set('rs', short)
    window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}${u.hash}`)
  } catch (e) { /* the address stays */ }
}

onMounted(() => {
  track('lesson_view', { item_id: props.lesson.short_uuid, via: props.via })
  loadRelated(props.lesson.short_uuid)
})
</script>

<template>
  <main ref="root" class="prp" :class="{ sheet }" data-testid="public-reading-page">
    <header v-if="!sheet" class="prp-top">
      <button type="button" class="prp-home" data-testid="public-reading-home" @click="emit('leave')">
        <span class="prp-mark" aria-hidden="true">✦</span><span>Mystic Aida</span>
      </button>
      <button v-if="!signedIn" type="button" class="prp-signin" data-testid="lesson-sign-in" @click="emit('login')">Sign in</button>
    </header>

    <div :key="shown.short_uuid" class="prp-paper" :class="{ swap: swapped, fading }">
      <PublicLetter :lesson="shown" byline flush>
        <div class="prp-respond">
          <button type="button" class="prp-res" :class="{ on: spoke }" :aria-pressed="spoke ? 'true' : 'false'" :disabled="busy"
                  data-testid="lesson-spoke" @click="toggleSpoke">
            <span class="st" aria-hidden="true">✦</span> {{ SPOKE_LABEL }}<span v-if="spokeCount" class="prp-count" data-testid="lesson-spoke-count">· {{ spokeCount }}</span>
          </button>
        </div>
        <LessonEnding :lesson="shown" @draw="onDraw" @own="onOwn" />
        <RelatedLessons :lesson="shown" :items="related" @open="openRelated" />
      </PublicLetter>
    </div>
  </main>
</template>

<style scoped>
.prp {
  --cream2: #fbf6ec; --gold: #c9a94e; --amethyst: #4e2776; --muted: #7a6a8f;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  min-height: 100dvh; box-sizing: border-box; padding: 0 10px calc(28px + env(safe-area-inset-bottom, 0px));
  background: radial-gradient(120% 60% at 50% 0%, #1d0f38 0%, #0d0520 60%) #0d0520; color: #efe4ff;
  font-family: var(--ui); overflow-x: hidden;
}
.prp *, .prp *::before, .prp *::after { box-sizing: border-box; }
.prp-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; max-width: 1000px; margin: 0 auto; padding: 6px 2px 8px; }
.prp-home {
  display: inline-flex; align-items: center; gap: 10px; min-height: 44px; padding: 0 4px; border: 0; background: none; cursor: pointer;
  font: 600 13px 'Letter Display', 'Cinzel', Georgia, serif; letter-spacing: .14em; text-transform: uppercase; color: #f0d9a3;
}
.prp-mark { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; background: #2a1845; color: #e7c983; font-size: 13px; border: 1px solid rgba(231, 201, 131, .4); }
.prp-signin { min-height: 44px; padding: 0 8px; border: 0; background: none; cursor: pointer; font: 500 13px var(--ui); color: #c3a9ff; }
/* One sheet of paper: the letter, the response, the Mystic's turn. */
.prp-paper {
  position: relative; max-width: 1000px; margin: 0 auto; background: #f4ecdc; border-radius: 18px; overflow: hidden;
  box-shadow: 0 30px 60px -30px rgba(0, 0, 0, .9); container-type: inline-size;
}
.prp-paper { transition: opacity .22s ease; }
.prp-paper.fading { opacity: 0; }
.prp-paper.swap { animation: prp-swap .32s ease-out; }
@keyframes prp-swap { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.prp-respond { display: flex; justify-content: center; padding: 26px 20px 30px; }
@container (min-width: 760px) { .prp-respond { padding: 36px 48px 44px; } }
.prp-res {
  display: inline-flex; align-items: center; gap: 8px; min-height: 46px; max-width: 100%; padding: 10px 20px; border-radius: 999px;
  border: 1px solid var(--gold); background: var(--cream2); color: var(--amethyst); font: 600 14px var(--ui); cursor: pointer;
}
.prp-res .prp-count { color: var(--muted); font-weight: 500; }
.prp-res .st { display: inline-block; transition: transform .5s cubic-bezier(.2, .8, .2, 1), color .2s; }
.prp-res.on { background: #f3e2b5; color: #8a6a2a; }
.prp-res.on .st { transform: rotate(144deg) scale(1.25); color: var(--gold); }
.prp-res:disabled { cursor: default; }
.prp-res:focus-visible, .prp-home:focus-visible, .prp-signin:focus-visible { outline: 2px solid #e7c983; outline-offset: 2px; }
/* In the sheet the paper is the sheet: no page around it. */
.prp.sheet { min-height: 0; padding: 0; background: transparent; }
.prp.sheet .prp-paper { max-width: none; border-radius: 0; box-shadow: none; }
@media (min-width: 768px) {
  .prp:not(.sheet) { padding: 0 28px 48px; }
  .prp-top { padding: 14px 0 22px; }
}
@media (prefers-reduced-motion: reduce) { .prp-paper.swap { animation: none; } .prp-paper, .prp-res .st { transition: none; } }
</style>
